import React, { useState, useEffect } from 'react';
import { Trophy, Target, Clock, TrendingUp, Star, Award, Loader } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Progress as ProgressBar } from './ui/progress';
import { Badge } from './ui/badge';
import { progressAPI, categoriesAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';

const Progress = () => {
  const [progressData, setProgressData] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    loadProgressData();
    loadCategories();
  }, []);

  const loadProgressData = async () => {
    try {
      const data = await progressAPI.get();
      setProgressData(data);
    } catch (error) {
      toast({
        title: "Error Loading Progress",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const categoriesData = await categoriesAPI.getAll();
      setCategories(categoriesData || []);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="text-center py-12">
          <Loader className="w-8 h-8 animate-spin mx-auto mb-4 text-green-600" />
          <p className="text-gray-600">Loading your progress...</p>
        </div>
      </div>
    );
  }

  if (!progressData) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="text-center py-12">
          <p className="text-gray-600">Unable to load progress data</p>
        </div>
      </div>
    );
  }

  const { progress: userProgress, totalWords, learnedWords, overallProgress, weeklyStats, achievements: userAchievements } = progressData;

  const achievements = [
    { id: 1, title: 'First Steps', description: 'Learned your first 5 words', completed: userAchievements.includes('First Steps'), icon: Star },
    { id: 2, title: 'Food Explorer', description: 'Mastered all food vocabulary', completed: userAchievements.includes('Food Explorer'), icon: Award },
    { id: 3, title: 'Conversation Starter', description: 'Learned all greetings', completed: userAchievements.includes('Conversation Starter'), icon: Trophy },
    { id: 4, title: 'Family Matters', description: 'Complete family vocabulary', completed: userAchievements.includes('Family Matters'), icon: Target }
  ];

  const getProgressColor = (percentage) => {
    if (percentage >= 80) return 'text-green-600';
    if (percentage >= 60) return 'text-yellow-600';
    if (percentage >= 40) return 'text-orange-600';
    return 'text-red-600';
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2 font-heading">Learning Progress</h1>
        <p className="text-gray-600">Track your journey learning Algerian dialect</p>
      </div>

      {/* Overall Progress */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <Card className="md:col-span-2 border-0 shadow-md">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <TrendingUp className="w-5 h-5 text-green-600" />
              <span>Overall Progress</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="mb-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-2xl font-bold text-gray-900">{overallProgress}%</span>
                <span className="text-gray-600">{learnedWords} of {totalWords} words</span>
              </div>
              <ProgressBar value={overallProgress} className="h-3" />
            </div>
            <p className="text-gray-600">
              You&apos;re making great progress! Keep up the excellent work.
            </p>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-md">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Clock className="w-5 h-5 text-blue-600" />
              <span>This Week</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-900 mb-2">{weeklyStats.wordsLearned || 0}</div>
            <p className="text-gray-600 text-sm">Words learned</p>
            <div className="text-2xl font-bold text-gray-900 mb-2 mt-4">{weeklyStats.gamesCompleted || 0}</div>
            <p className="text-gray-600 text-sm">Games completed</p>
          </CardContent>
        </Card>
      </div>

      {/* Category Progress */}
      <Card className="mb-8 border-0 shadow-md">
        <CardHeader>
          <CardTitle>Progress by Category</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {Object.entries(userProgress).map(([categoryId, progress]) => {
              const category = categories.find(cat => cat.id === categoryId);
              return (
                <div key={categoryId} className="flex items-center space-x-4">
                  <div className="flex items-center space-x-3 w-32">
                    <span className="text-xl">{category?.icon}</span>
                    <span className="font-medium text-gray-900">{category?.name}</span>
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between items-center mb-1">
                      <span className={`font-semibold ${getProgressColor(progress.percentage)}`}>
                        {progress.percentage}%
                      </span>
                      <span className="text-sm text-gray-500">
                        {progress.learned}/{progress.total}
                      </span>
                    </div>
                    <ProgressBar 
                      value={progress.percentage} 
                      className="h-2"
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Achievements */}
      <Card className="border-0 shadow-md">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Trophy className="w-5 h-5 text-yellow-600" />
            <span>Achievements</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {achievements.map((achievement) => {
              const IconComponent = achievement.icon;
              return (
                <div
                  key={achievement.id}
                  className={`flex items-center space-x-4 p-4 rounded-lg transition-all duration-200 ${
                    achievement.completed
                      ? 'bg-green-50 border border-green-200'
                      : 'bg-gray-50 border border-gray-200'
                  }`}
                >
                  <div className={`flex items-center justify-center w-12 h-12 rounded-full ${
                    achievement.completed
                      ? 'bg-green-100 text-green-600'
                      : 'bg-gray-100 text-gray-400'
                  }`}>
                    <IconComponent className="w-6 h-6" />
                  </div>
                  <div className="flex-1">
                    <h3 className={`font-semibold ${
                      achievement.completed ? 'text-gray-900' : 'text-gray-500'
                    }`}>
                      {achievement.title}
                    </h3>
                    <p className={`text-sm ${
                      achievement.completed ? 'text-gray-600' : 'text-gray-400'
                    }`}>
                      {achievement.description}
                    </p>
                  </div>
                  {achievement.completed && (
                    <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
                      Completed
                    </Badge>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Study Streak */}
      <div className="mt-8 text-center bg-gradient-to-r from-green-50 to-red-50 rounded-2xl p-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Study Streak</h2>
        {progressData?.streak ? (
          <>
            <div className="text-4xl font-bold bg-gradient-to-r from-green-600 to-red-600 bg-clip-text text-transparent mb-2">
              {progressData.streak.currentStreak} {progressData.streak.currentStreak === 1 ? 'Day' : 'Days'}
            </div>
            <p className="text-gray-600 mb-4">
              {progressData.streak.currentStreak === 0 
                ? "Start studying today to begin your streak!" 
                : progressData.streak.currentStreak >= 7 
                  ? "🔥 Amazing! You are on fire!" 
                  : progressData.streak.currentStreak >= 3 
                    ? "Keep it up! You are building a great habit." 
                    : "Great start! Keep the momentum going."}
            </p>
            {progressData.streak.longestStreak > 0 && (
              <p className="text-sm text-gray-500 mb-4">
                Your longest streak: {progressData.streak.longestStreak} days
              </p>
            )}
            <div className="flex justify-center space-x-2">
              {[...Array(7)].map((_, index) => {
                const isActive = index < progressData.streak.currentStreak;
                return (
                  <div
                    key={index}
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-all ${
                      isActive 
                        ? 'bg-gradient-to-br from-green-500 to-red-500 text-white' 
                        : 'bg-gray-200 text-gray-400'
                    }`}
                  >
                    {index + 1}
                  </div>
                );
              })}
            </div>
            {progressData.streak.lastStudyDate && (
              <p className="text-xs text-gray-400 mt-4">
                Last studied: {new Date(progressData.streak.lastStudyDate).toLocaleDateString()}
              </p>
            )}
          </>
        ) : (
          <>
            <div className="text-4xl font-bold text-gray-300 mb-2">0 Days</div>
            <p className="text-gray-600 mb-4">Start studying to begin your streak!</p>
            <div className="flex justify-center space-x-2">
              {[1, 2, 3, 4, 5, 6, 7].map((day) => (
                <div
                  key={day}
                  className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center text-gray-400 text-sm font-semibold"
                >
                  {day}
                </div>
              ))}
            </div>
          </>
        )}

        {/* Competition Banner */}
        <div className="mt-8 bg-gradient-to-r from-amber-100 via-yellow-50 to-amber-100 border-2 border-amber-300 rounded-xl p-6 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-24 h-24 bg-amber-200 rounded-full -mr-8 -mt-8 opacity-50"></div>
          <div className="absolute bottom-0 left-0 w-16 h-16 bg-amber-200 rounded-full -ml-4 -mb-4 opacity-50"></div>
          
          <div className="relative z-10">
            <div className="flex items-center justify-center mb-3">
              <Trophy className="w-8 h-8 text-amber-600 mr-2" />
              <h3 className="text-xl font-bold text-amber-800">7-Day Streak Competition!</h3>
              <Trophy className="w-8 h-8 text-amber-600 ml-2" />
            </div>
            
            <p className="text-amber-900 font-medium mb-4">
              Reach a 7-day study streak and you will be <span className="font-bold">automatically entered</span> to win:
            </p>
            
            <div className="bg-white/70 rounded-lg p-4 mb-4">
              <ul className="text-left text-amber-900 space-y-2">
                <li className="flex items-center">
                  <Star className="w-5 h-5 text-amber-500 mr-2 flex-shrink-0" />
                  <span><strong>Speak Algerian Ultimate Video Course Package</strong></span>
                </li>
                <li className="flex items-center">
                  <Star className="w-5 h-5 text-amber-500 mr-2 flex-shrink-0" />
                  <span><strong>ALL Speak Algerian Learning PDFs</strong></span>
                </li>
              </ul>
            </div>
            
            {progressData?.streak?.currentStreak >= 7 ? (
              <div className="bg-green-100 border border-green-300 rounded-lg p-3">
                <p className="text-green-800 font-bold flex items-center justify-center">
                  <Award className="w-5 h-5 mr-2" />
                  You are entered! Good luck!
                </p>
              </div>
            ) : (
              <p className="text-amber-700 text-sm">
                {progressData?.streak?.currentStreak > 0 
                  ? `Only ${7 - progressData.streak.currentStreak} more day${7 - progressData.streak.currentStreak === 1 ? '' : 's'} to go!`
                  : "Start your streak today for a chance to win!"
                }
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Progress;